import NOTIFICATIONS from 'components/ToastNotification/constants';
import { useContext, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError, scrollToId } from 'utils/helpers';
import { MembershipTypeOptions } from 'utils/types';

import { NotificationContext } from '../../../containers/Notification';
import { UserContext } from '../useUserContext/context';
import StoreApi from './StoreApi';
import {
  TestPlanItem,
  TestPlanItemsResponse,
  TestPlanStatusIds,
  TestPlanTypes,
  TestsClassification,
} from './types';

export const useTestPlan = (): TestPlanTypes => {
  const { setNotificationData } = useContext(NotificationContext);
  const {
    userData: { userId, loggedIn, isMember, membershipType },
  } = useContext(UserContext);

  // Current active status

  const [testPlanActiveStatus, _setTestPlanActiveStatus] =
    useState<TestPlanStatusIds>(TestPlanStatusIds.upcoming);

  // TEST PLAN ID
  const [testPlanId, setTestPlanId] = useState<string>('');

  // UPCOMING TESTS
  const [upcomingTests, setUpcomingTest] = useState<TestPlanItem[]>([]);

  // IN PROGRESS TESTS
  const [inProgressTests, setInProgressTest] = useState<TestPlanItem[]>([]);

  // COMPLETED TESTS
  const [completedTests, setCompletedTest] = useState<TestPlanItem[]>([]);

  // Loading flags
  const [loadingTestPlanItems, setLoadingTestPlanItems] = useState(false);

  const endpoints = {
    TEST_PLAN: `v1/users/${userId}/test_plan`,
    TEST_PLAN_ITEM: (testPlanId: string, itemId: string | number) =>
      `v1/users/${userId}/test_plans/${testPlanId}/test_plan_items/${itemId}`,
    TEST_PLAN_ITEMS: (testPlanId: string) =>
      `v1/users/${userId}/test_plans/${testPlanId}/test_plan_items`,
  };

  const setTestPlanActiveStatus = (status: TestPlanStatusIds) => {
    _setTestPlanActiveStatus(status);
  };

  const setTestPlanItems = (res: TestPlanItemsResponse) => {
    const testsClassification: TestsClassification = {
      upcoming: [],
      in_progress: [],
      completed: [],
    };
    // Group by test status
    res.data.map(
      (test: TestPlanItem) =>
        test.attributes.state &&
        testsClassification[test.attributes.state].push(test),
    );

    setUpcomingTest(testsClassification.upcoming.reverse());
    setInProgressTest(testsClassification.in_progress.reverse());
    setCompletedTest(testsClassification.completed.reverse());
  };

  const getUserTestPlan = async () => {
    try {
      if (!userId || !loggedIn) return;
      const {
        data: { data },
      } = await StoreApi.get(endpoints.TEST_PLAN);
      setTestPlanId(data.id);
      return data.id;
    } catch (err) {
      await analytics.track({
        event: ANALYTICS?.EVENTS?.API_FAILURE,
        data: {
          label: 'Fetch Failed',
          component: ANALYTICS?.LABELS.TEST_PLAN?.label,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'useTestPlan',
        method: 'getUserTestPlan',
      });
      return Promise.reject(err);
    }
  };

  const getTestPlanItems = async () => {
    try {
      setLoadingTestPlanItems(true);

      const _testPlanId = testPlanId ?? (await getUserTestPlan());
      if (!_testPlanId) return;
      const { data } = await StoreApi.get(
        `${endpoints.TEST_PLAN_ITEMS(_testPlanId)}`,
      );
      setTestPlanItems(data);
      return data;
    } catch (err) {
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Fetch Failed',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'useTestPlan',
        method: 'getTestPlanItems',
      });
      return Promise.reject(err);
    } finally {
      setLoadingTestPlanItems(false);
    }
  };

  const createTestPlanItem = async (spreeVariantId: string | number) => {
    try {
      setLoadingTestPlanItems(true);
      if (!testPlanId) await getUserTestPlan();
      const payload = {
        test_plan_id: testPlanId,
        spree_variant_id: spreeVariantId,
      };
      const _testPlanId = testPlanId ?? (await getUserTestPlan());
      if (!_testPlanId) return;
      const {
        data: { data },
      } = await StoreApi.post(
        `${endpoints.TEST_PLAN_ITEMS(_testPlanId)}`,
        payload,
      );

      const sku = data?.attributes?.sku ?? '';
      setUpcomingTest((items) => [data, ...items]);
      setNotificationData(
        NOTIFICATIONS.MBRS_TEST_PLAN_ITEM_CREATION_SUCCESS('useTestPlan'),
      );
      await setTestPlanActiveStatus(TestPlanStatusIds.upcoming);
      scrollToId(
        `${
          membershipType == MembershipTypeOptions.current //eslint-disable-line
            ? 'current'
            : 'control'
        }-membership-test-plan`,
      );
      await analytics.track({
        event: ANALYTICS.EVENTS.API_SUCCESS,
        data: {
          label: ANALYTICS.LABELS.TEST_PLAN.api.add,
          description: 'Fetch success',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
          ...(sku && { sku }),
        },
      });
      return data;
    } catch (err) {
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: ANALYTICS.LABELS.TEST_PLAN.api.add,
          description: 'Fetch Failed',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'useTestPlan',
        method: 'createTestPlanItem',
      });
      setNotificationData(
        NOTIFICATIONS.MBRS_TEST_PLAN_ITEM_CREATION_FAILURE('useTestPlan'),
      );
      return Promise.reject(err);
    } finally {
      setLoadingTestPlanItems(false);
    }
  };

  const updateTestPlanItem = async (testPlanItem: TestPlanItem) => {
    try {
      setLoadingTestPlanItems(true);
      const _testPlanId = testPlanId ?? (await getUserTestPlan());
      if (!_testPlanId) return;
      await StoreApi.patch(
        `${endpoints.TEST_PLAN_ITEM(_testPlanId, testPlanItem.id)}`,
        {
          state: testPlanItem.attributes.state,
          position: testPlanItem.attributes.position,
        },
      );
      await getTestPlanItems();
    } catch (err) {
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Fetch Failed',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'useTestPlan',
        method: 'updateTestPlanItem',
      });
      return Promise.reject(err);
    } finally {
      setLoadingTestPlanItems(false);
    }
  };

  const removeTestPlanItem = async (id: string | number) => {
    try {
      setLoadingTestPlanItems(true);
      const _testPlanId = testPlanId ?? (await getUserTestPlan());
      if (!_testPlanId) return;
      const { data } = await StoreApi.delete(
        `${endpoints.TEST_PLAN_ITEM(_testPlanId, id)}`,
      );
      await getTestPlanItems();
      setNotificationData(
        NOTIFICATIONS.MBRS_REMOVE_TEST_PLAN_ITEM_CREATION_SUCCESS(
          'useTestPlan',
        ),
      );

      await analytics.track({
        event: ANALYTICS.EVENTS.API_SUCCESS,
        data: {
          label: ANALYTICS.LABELS.TEST_PLAN.api.remove,
          description: 'POST success',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
        },
      });

      return data;
    } catch (err) {
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: ANALYTICS.LABELS.TEST_PLAN.api.remove,
          description: 'Fetch Failed',
          component: ANALYTICS.LABELS.TEST_PLAN.label,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'useTestPlan',
        method: 'removeTestPlanItem',
      });
      setNotificationData(
        NOTIFICATIONS.MBRS_REMOVE_TEST_PLAN_ITEM_FAILURE('useTestPlan'),
      );
      return Promise.reject(err);
    } finally {
      setLoadingTestPlanItems(false);
    }
  };

  useEffect(() => {
    if (testPlanId) {
      getTestPlanItems();
    } else {
      getUserTestPlan();
    }
  }, [testPlanId, userId, isMember]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getUserTestPlan();
  }, [testPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    upcomingTests,
    inProgressTests,
    completedTests,
    loadingTestPlanItems,
    testPlanActiveStatus,
    getTestPlanItems,
    createTestPlanItem,
    updateTestPlanItem,
    removeTestPlanItem,
    setTestPlanActiveStatus,
  };
};

export default useTestPlan;
